import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Search, Mic, ChevronRight, BookOpen, Plus } from 'lucide-react';
import BackToTop from './BackToTop';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dream Journal — Dreams V1 composition, brought into the accepted Arvexis visual family
// (Phase 6 Dream visual closeout, 2026-06). Presentation only: the information hierarchy,
// the ordering, the cards, the navigation and every request below are unchanged.
// Canonical vocabulary: the member creates Dreams, Arvexis creates Reflections, together they are
// the Dream Journal. Nothing here is labelled "journal entry" or "interpretation".
const COPY = {
  en: {
    title: 'Dream Journal',
    subtitle: 'A private record of what stays with you.',
    dreams: 'dreams',
    dream: 'dream',
    commonEmotion: 'most common emotion',
    recurring: 'recurring pattern',
    recent: 'Recent',
    patterns: 'Patterns',
    search: 'Search your dreams…',
    record: 'Record a dream',
    latest: 'LATEST DREAM',
    returnTo: 'Return to this dream',
    empty: 'No dreams recorded yet',
    emptySub: 'Your first dream begins here.',
    noResults: 'No dreams match that search',
    noResultsSub: 'Try a different word, or clear the search to see everything.',
    clear: 'Clear search',
    recurringLately: 'Recurring lately',
    appearedIn: (n) => `Appeared in ${n} dreams`,
    recurrenceLine: 'Sometimes the same images find their way back.',
    privacy: 'Private by default.',
    none: '—',
  },
  es: {
    title: 'Diario de Sueños',
    subtitle: 'Un registro privado de lo que se queda contigo.',
    dreams: 'sueños',
    dream: 'sueño',
    commonEmotion: 'emoción más común',
    recurring: 'patrón recurrente',
    recent: 'Recientes',
    patterns: 'Patrones',
    search: 'Busca en tus sueños…',
    record: 'Registra un sueño',
    latest: 'ÚLTIMO SUEÑO',
    returnTo: 'Volver a este sueño',
    empty: 'Aún no hay sueños registrados',
    emptySub: 'Tu primer sueño comienza aquí.',
    noResults: 'Ningún sueño coincide con esa búsqueda',
    noResultsSub: 'Prueba otra palabra, o borra la búsqueda para verlo todo.',
    clear: 'Borrar búsqueda',
    recurringLately: 'Últimamente se repite',
    appearedIn: (n) => `Apareció en ${n} sueños`,
    recurrenceLine: 'A veces, las mismas imágenes encuentran el camino de regreso.',
    privacy: 'Privado por defecto.',
    none: '—',
  },
};

const THUMB_FALLBACK = '/artwork/thumbs/reflective-001.jpg';

// The accepted visual family, used verbatim: canonical deep midnight, restrained champagne,
// ivory and soft silver. Nothing here invents a colour.
const C = {
  ivory: '#F4EFE7',
  silver: 'rgba(244, 239, 231, 0.56)',
  quiet: 'rgba(244, 239, 231, 0.42)',
  faint: 'rgba(244, 239, 231, 0.28)',
  champagne: '#E8C58F',
  hairline: 'rgba(244, 239, 231, 0.09)',
  glass: 'rgba(255, 255, 255, 0.028)',
};

// The accepted primary action: a champagne hairline over a soft champagne wash with an ivory
// label — the same treatment Home's primary actions already use. Not a solid brass fill.
const PRIMARY_ACTION = {
  background: 'rgba(232, 197, 143, 0.06)',
  border: '1px solid rgba(232, 197, 143, 0.56)',
  color: C.ivory,
  boxShadow: '0 0 12px rgba(232, 197, 143, 0.14)',
};

// Emotion is a canonical key, not member prose — so it is shown in the reading language.
// Symbols and Dream text are the member's own content and are never rewritten here.
const EMOTION_ES = {
  calm: 'Tranquilo', anxious: 'Ansioso', sad: 'Triste', confused: 'Confundido',
  peaceful: 'Pacífico', fearful: 'Temeroso', hopeful: 'Esperanzado', curious: 'Curioso',
  nostalgic: 'Nostálgico', unsettled: 'Inquieto', neutral: 'Neutral', joyful: 'Alegre',
  angry: 'Enojado', surprised: 'Sorprendido', melancholic: 'Melancólico', excited: 'Emocionado',
  grateful: 'Agradecido', lonely: 'Solitario', empowered: 'Empoderado', overwhelmed: 'Abrumado',
  serene: 'Sereno', frustrated: 'Frustrado', content: 'Contento', terrified: 'Aterrado',
  inspired: 'Inspirado', vulnerable: 'Vulnerable', detached: 'Desapegado', mysterious: 'Misterioso',
  determined: 'Determinado', liberated: 'Liberado', connected: 'Conectado', reflective: 'Reflexivo',
};

const emotionLabel = (emotion, lang) => {
  if (!emotion) return null;
  const label = lang === 'es' ? EMOTION_ES[emotion.toLowerCase().trim()] || emotion : emotion;
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const thumbFor = (dream) => {
  if (dream.artwork_image_id) return `${API}/images/${dream.artwork_image_id}?w=400`;
  const key = (dream.emotion || '').toLowerCase();
  return key ? `/artwork/thumbs/${key}-001.jpg` : THUMB_FALLBACK;
};

const symbolNames = (dream) =>
  (dream.symbols || []).map((s) => (typeof s === 'string' ? s : s?.name)).filter(Boolean);

const DreamCard = ({ dream, t, lang, latest, onOpen, isDesktop }) => {
  const date = new Date(dream.created_at).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', {
    month: 'long',
    day: 'numeric',
  });
  const emotion = emotionLabel(dream.emotion, lang);

  return (
    <motion.button
      type="button"
      onClick={onOpen}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full text-left rounded-2xl overflow-hidden transition-colors"
      style={{
        background: C.glass,
        border: `1px solid ${C.hairline}`,
        backdropFilter: 'blur(10px)',
      }}
      data-testid={latest ? 'dream-card-latest' : 'dream-card'}
      data-dream-id={dream.id}
    >
      <div
        className="flex"
        style={
          latest
            ? { flexDirection: isDesktop ? 'row' : 'column', alignItems: 'stretch' }
            : { alignItems: 'stretch', gap: 16 }
        }
      >
        <div
          className="shrink-0"
          style={latest ? { width: isDesktop ? '46%' : '100%' } : { width: 118 }}
        >
          <img
            src={thumbFor(dream)}
            onError={(e) => {
              if (e.currentTarget.src.indexOf(THUMB_FALLBACK) === -1) e.currentTarget.src = THUMB_FALLBACK;
            }}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="w-full h-full object-cover"
            style={
              latest
                ? (isDesktop
                    ? { height: '100%', minHeight: 300, maxHeight: 340 }
                    : { aspectRatio: '4 / 3' })
                : { height: '100%', minHeight: 132 }
            }
          />
        </div>

        <div
          className={latest ? '' : 'py-4 pr-4 flex-1 min-w-0'}
          style={
            latest
              ? isDesktop
                ? { padding: 34, display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1, minWidth: 0 }
                : { padding: 20 }
              : undefined
          }
        >
          {latest && (
            <p
              className="text-[10px] mb-2.5"
              style={{ color: C.champagne, letterSpacing: '0.26em', fontWeight: 600 }}
            >
              {t.latest}
            </p>
          )}
          <h3
            className={`font-medium leading-snug ${latest ? 'text-xl md:text-3xl' : 'text-base md:text-lg'}`}
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: C.ivory }}
            data-testid="dream-card-title"
          >
            {dream.title ? (
              dream.title
            ) : (
              // Dreams recorded before titles existed have none. Rather than inventing one, the
              // member's own opening words stand in, in italics so they read as their words.
              <span className="italic" style={{ color: 'rgba(244, 239, 231, 0.8)' }}>
                {`“${(dream.transcript || '').split(/\s+/).slice(0, 8).join(' ')}…”`}
              </span>
            )}
          </h3>
          <p className="mt-1.5 text-xs" style={{ color: C.quiet }}>
            {emotion ? `${emotion} • ${date}` : date}
          </p>
          <p className="mt-2 text-sm line-clamp-2" style={{ color: C.silver }}>
            {dream.transcript}
          </p>

          {latest ? (
            <span
              className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium"
              style={{ color: C.champagne }}
            >
              {t.returnTo}
              <ChevronRight className="w-4 h-4" />
            </span>
          ) : null}
        </div>

        {!latest && (
          <div className="flex items-center pr-4">
            <ChevronRight className="w-4 h-4" style={{ color: C.faint }} />
          </div>
        )}
      </div>
    </motion.button>
  );
};

export const DreamJournal = ({ lang = 'en' }) => {
  const navigate = useNavigate();
  const t = COPY[lang === 'es' ? 'es' : 'en'];
  const [dreams, setDreams] = useState([]);
  const [baseDreams, setBaseDreams] = useState([]);
  const [allCount, setAllCount] = useState(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  // The floating Record affordance is for reach on small screens only — on desktop the same
  // single action lives in the header, so there is never more than one of it on screen.
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    setIsDesktop(mq.matches);
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const params = query ? `?search=${encodeURIComponent(query)}` : '';
        const res = await axios.get(`${API}/dreams${params}`);
        if (cancelled) return;
        setDreams(res.data || []);
        if (!query) {
          setBaseDreams(res.data || []);
          setAllCount((res.data || []).length);
        }
      } catch (e) {
        if (!cancelled) setDreams([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const id = setTimeout(run, query ? 300 : 0);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [query]);

  // Every number below is counted from the member's real Dreams. Nothing is inferred or invented.
  const stats = useMemo(() => {
    const source = baseDreams;
    const emotions = {};
    const symbols = {};
    source.forEach((d) => {
      if (d.emotion) emotions[d.emotion] = (emotions[d.emotion] || 0) + 1;
      new Set(symbolNames(d).map((s) => s.toLowerCase())).forEach((s) => {
        symbols[s] = (symbols[s] || 0) + 1;
      });
    });
    const top = (obj) => Object.entries(obj).sort((a, b) => b[1] - a[1])[0];
    const recurring = Object.entries(symbols)
      .filter(([, n]) => n >= 2)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
    return { count: source.length, topEmotion: top(emotions), recurring };
  }, [baseDreams]);

  const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : t.none);
  const isSearching = query.trim().length > 0;

  return (
    <div
      /* The one canonical atmosphere, the same asset Home, Message, Settings and Profile use.
         The phone's bespoke flat near-black and the local gold wash are gone. */
      className="min-h-screen relative utility-surface utility-surface--atmospheric"
      data-testid="dream-journal"
    >
      <div className="relative max-w-[1180px] mx-auto px-5 md:px-10 pt-8 pb-6">
        <div className="flex items-start justify-between gap-6">
        <div>
        {!isDesktop && (
          <div className="flex items-center justify-between mb-8" data-testid="journal-mobile-topbar">
            <span
              className="text-[12px]"
              style={{ letterSpacing: '0.3em', fontFamily: "'Cormorant Garamond', Georgia, serif",
                       color: 'rgba(244, 239, 231, 0.8)' }}
            >
              ARVEXIS
            </span>
            {/* Verified existing entry point: this is Record a Dream. Presentation is reconciled
                with the accepted family — it is no longer a solid brass disc. Behaviour, route
                and testid are untouched. */}
            <button
              type="button"
              onClick={() => navigate('/log')}
              className="w-11 h-11 rounded-full flex items-center justify-center transition-colors"
              style={PRIMARY_ACTION}
              aria-label={t.record}
              title={t.record}
              data-testid="journal-add-btn"
            >
              <Plus className="w-5 h-5" style={{ color: C.champagne }} strokeWidth={1.6} />
            </button>
          </div>
        )}

        <h1
          className="utility-head__title"
          style={{ fontSize: isDesktop ? undefined : '34px' }}
          data-testid="dream-journal-title"
        >
          {t.title}
        </h1>
        <p className="utility-head__sub">{t.subtitle}</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/log')}
          className="hidden lg:inline-flex items-center gap-2.5 shrink-0 px-7 py-3.5 rounded-full text-sm font-medium transition-colors"
          style={PRIMARY_ACTION}
          data-testid="record-dream-btn-desktop"
        >
          <Mic className="w-4 h-4" style={{ color: C.champagne }} strokeWidth={1.6} />
          {t.record}
        </button>
        </div>

        {/* Real counts only */}
        <div className="flex items-stretch gap-6 md:gap-10 mt-9" data-testid="dream-journal-stats">
          <div>
            <p className="text-2xl md:text-3xl leading-none" style={{ color: C.ivory }}>
              {allCount ?? '–'}
            </p>
            <p className="text-[11px] mt-2" style={{ color: C.quiet }}>
              {allCount === 1 ? t.dream : t.dreams}
            </p>
          </div>
          <div className="w-px" style={{ background: C.hairline }} />
          <div>
            <p className="text-lg md:text-xl leading-none pt-1" style={{ color: C.ivory }}>
              {stats.topEmotion ? emotionLabel(stats.topEmotion[0], lang) : t.none}
            </p>
            <p className="text-[11px] mt-2" style={{ color: C.quiet }}>{t.commonEmotion}</p>
          </div>
          <div className="w-px" style={{ background: C.hairline }} />
          <div>
            <p className="text-lg md:text-xl leading-none pt-1" style={{ color: C.ivory }}>
              {stats.recurring[0] ? cap(stats.recurring[0][0]) : t.none}
            </p>
            <p className="text-[11px] mt-2" style={{ color: C.quiet }}>{t.recurring}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-9">
          <span
            className="px-4 py-1.5 rounded-full text-xs"
            style={{ background: 'rgba(232, 197, 143, 0.08)',
                     border: '1px solid rgba(232, 197, 143, 0.28)',
                     color: C.champagne }}
            data-testid="tab-recent"
          >
            {t.recent}
          </span>
        </div>

        <div className="relative mt-5">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2" style={{ color: C.faint }} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.search}
            className="w-full rounded-xl pl-11 pr-4 py-3.5 text-sm outline-none transition-colors"
            style={{ background: C.glass, border: `1px solid ${C.hairline}`, color: C.ivory }}
            data-testid="search-input"
          />
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-8 lg:gap-10 items-start">
          <div>
            {loading ? (
              <p className="text-sm py-16 text-center" style={{ color: C.faint }}>…</p>
            ) : dreams.length === 0 ? (
              <div className="py-20 text-center" data-testid={isSearching ? 'no-results-state' : 'empty-state'}>
                <div
                  className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center mb-5"
                  style={{ background: C.glass, border: `1px solid ${C.hairline}` }}
                >
                  <BookOpen className="w-7 h-7" style={{ color: C.faint }} strokeWidth={1.4} />
                </div>
                <p style={{ color: C.silver }}>{isSearching ? t.noResults : t.empty}</p>
                <p className="text-sm mt-1.5" style={{ color: C.quiet }}>
                  {isSearching ? t.noResultsSub : t.emptySub}
                </p>
                {isSearching ? (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className="mt-6 px-5 py-2.5 rounded-full text-sm"
                    style={{ border: '1px solid rgba(232, 197, 143, 0.4)', color: C.champagne }}
                    data-testid="clear-search-btn"
                  >
                    {t.clear}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate('/log')}
                    className="mt-6 inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full text-sm font-medium"
                    style={PRIMARY_ACTION}
                    data-testid="empty-log-btn"
                  >
                    <Mic className="w-4 h-4" style={{ color: C.champagne }} strokeWidth={1.6} />
                    {t.record}
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {!isSearching && (
                  <DreamCard
                    dream={dreams[0]}
                    t={t}
                    lang={lang}
                    latest
                    isDesktop={isDesktop}
                    onOpen={() => navigate(`/reflection/${dreams[0].id}`)}
                  />
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(isSearching ? dreams : dreams.slice(1)).map((d) => (
                    <DreamCard
                      key={d.id}
                      dream={d}
                      t={t}
                      lang={lang}
                      onOpen={() => navigate(`/reflection/${d.id}`)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Observations, never conclusions — and only when the data actually supports them.
              Authority traced 2026-06: every row is counted from the member's own Dream
              `symbols`, returned by GET /api/dreams. Nothing here is inferred or generated.
              Behaviour and threshold are unchanged; only the presentation is reconciled. */}
          {stats.recurring.length > 0 && (
            <aside className="hidden lg:block" data-testid="recurring-panel">
              <div
                className="rounded-2xl p-5"
                style={{ background: C.glass, border: `1px solid ${C.hairline}`,
                         backdropFilter: 'blur(10px)' }}
              >
                <p
                  className="text-[10.5px] mb-4"
                  style={{ letterSpacing: '0.24em', color: 'rgba(232, 197, 143, 0.78)',
                           fontWeight: 600 }}
                >
                  {t.recurringLately.toUpperCase()}
                </p>
                <ul className="space-y-3.5">
                  {stats.recurring.map(([name, n]) => (
                    <li key={name}>
                      <p className="text-sm" style={{ color: C.ivory }}>{cap(name)}</p>
                      <p className="text-xs mt-0.5" style={{ color: C.quiet }}>{t.appearedIn(n)}</p>
                    </li>
                  ))}
                </ul>
              </div>
              <p
                className="mt-5 px-1 text-sm italic"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: C.silver }}
              >
                “{t.recurrenceLine}”
              </p>
              <p className="mt-5 px-1 text-[11px]" style={{ color: C.faint }}>{t.privacy}</p>
            </aside>
          )}
        </div>
      </div>

      {/* Record a dream — one name for this action, everywhere */}
      <div
        className="justify-center px-6 pb-8"
        style={{ display: isDesktop ? 'none' : 'flex' }}
      >
        <button
          type="button"
          onClick={() => navigate('/log')}
          className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full text-sm font-medium"
          style={PRIMARY_ACTION}
          data-testid="record-dream-btn"
        >
          <Mic className="w-4 h-4" style={{ color: C.champagne }} strokeWidth={1.6} />
          {t.record}
        </button>
      </div>
      <BackToTop lang={lang} liftForNav />
    </div>
  );
};

export default DreamJournal;
